import React, { useState, useContext, useRef, useEffect } from 'react'
import axios from "axios";
import { useField } from 'formik';

import { getApiUrl } from '../../core/config/api.js';
import { Loading, Alert } from '../../components';
import { SettingsContext, UserContext, Window } from '../../contexts';
import { AjaxForm } from '../../forms';
import { MasterAdmin } from '../molecules';
import { useSetupTarget } from '../../onboarding/useSetupTarget';
import { Card, Field, Input, Textarea, Select, Button, Icon } from '../../components/ui';
import { SetupSummaryCard } from '../../onboarding/SetupSummaryCard';

import './dao-settings.scss';

// A field the checklist can link straight to: ?field=<name> scrolls to it and
// flashes it, so "Set your store web address" lands on that input rather than
// on a page of them.
function SetupField({ name, label, hint, required, children }) {
  const { targeted, ref } = useSetupTarget('field', name);
  return (
    <div ref={ref} data-setup-target={targeted ? 'true' : undefined} className="dao-settings__field">
      <Field label={label} hint={hint} required={required}>{children}</Field>
    </div>
  );
}

// Formik-wired field wrappers — these render as children of AjaxForm's <Formik>, so
// useField() sees the surrounding form context even though DaoSettings itself (the
// parent that creates the JSX) does not.
function TextField({ name, label, hint, required }) {
  const [field] = useField(name);
  return (
    <SetupField name={name} label={label} hint={hint} required={required}>
      <Input type="text" {...field} value={field.value ?? ''} />
    </SetupField>
  );
}

function AddressField({ name, label, hint, required }) {
  const [field] = useField(name);
  return (
    <SetupField name={name} label={label} hint={hint} required={required}>
      <Textarea {...field} value={field.value ?? ''} minRows={3} />
    </SetupField>
  );
}

function JurisdictionField({ name, label, options, hint, required, placeholder = "Select a country…" }) {
  const [field, , helpers] = useField(name);
  return (
    <SetupField name={name} label={label} hint={hint} required={required}>
      <Select
        options={options}
        value={field.value ?? null}
        onChange={(value) => helpers.setValue(value)}
        placeholder={placeholder}
        searchable
      />
    </SetupField>
  );
}

// One logo slot: a styled button over a hidden file input, with a live preview
// of the picked file (or the saved logo) until Save settings. Field clones its
// child and sets id / aria-describedby, so both land on the button.
function LogoPicker({ id, 'aria-describedby': describedBy, current, file, onPick, alt, small }) {
  const inputRef = useRef(null);
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!file) { setUrl(null); return undefined; }
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  const src = url || (current ? getApiUrl(`/api/logos/${current}`) : null);
  return (
    <div className="dao-settings__logo-picker">
      {src ? (
        <img className={`dao-settings__logo-preview${small ? ' dao-settings__logo-preview--sm' : ''}`} src={src} alt={alt} />
      ) : null}
      <Button
        id={id}
        aria-describedby={describedBy}
        variant="secondary"
        leadingIcon={<Icon name="Upload" size="sm" />}
        onClick={() => inputRef.current?.click()}
      >
        {src ? 'Replace' : 'Upload logo'}
      </Button>
      {file ? <span className="dao-settings__logo-name">{file.name}, saved when you press Save settings</span> : null}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        tabIndex={-1}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f); e.target.value = ''; }}
      />
    </div>
  );
}

export function DaoSettings() {
  const { settings, settingsLoaded } = useContext(SettingsContext);
  const { user, refresh } = useContext(UserContext);
  // The logos step links to the whole Logos card: four inputs that are one task.
  const logos = useSetupTarget('field', 'logos');
  const [desktop, setDesktop] = useState(null);
  const [mobile, setMobile] = useState(null);
  const [favicon, setFavicon] = useState(null);
  const [homescreen, setHomescreen] = useState(null);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [masterAdmin, setMasterAdmin] = useState(user.masterAdmin);
  const [masterAdminUpdated, setMasterAdminUpdated] = useState(0);

  if (!settingsLoaded) {
    return <Loading />;
  }

  const jurisdictionOptions = (Window.COUNTRIES || []).map((c) => ({ value: c.name, label: c.name }));
  // IANA zones from the browser (every current browser has Intl.supportedValuesOf).
  const timezoneOptions = (typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : ["Europe/London"])
    .map((z) => ({ value: z, label: z.replace(/_/g, " ") }));

  const clearFlags = () => {
    setSuccess('');
    setError('');
  }

  const submitForm = async (data) => {
    clearFlags();

    try {
      const formData = new FormData();

      if (desktop) {
        formData.append("desktop", desktop, desktop.name);
      }
      if (mobile) {
        formData.append("mobile", mobile, mobile.name);
      }
      if (favicon) {
        formData.append("favicon", favicon, favicon.name);
      }
      if (homescreen) {
        formData.append("homescreen", homescreen, homescreen.name);
      }
      if (data.name) {
        formData.append("name", data.name);
      }
      if (data.acronym) {
        formData.append("acronym", data.acronym);
      }
      if (data.physical_address) {
        formData.append("physical_address", data.physical_address);
      }
      if (data.store_url) {
        formData.append("store_url", data.store_url);
      }
      if (data.jurisdiction) {
        formData.append("jurisdiction", data.jurisdiction);
      }
      if (data.timezone) {
        formData.append("timezone", data.timezone);
      }
      if (data.support_whatsapp) {
        formData.append("support_whatsapp", data.support_whatsapp);
      }
      if (masterAdmin) {
        formData.append("master_admin", masterAdmin);
      }

      await axios.post("/api/app-settings", formData);

      if (masterAdmin) {
        refresh();
        setMasterAdminUpdated(current => current + 1);
      }

      setSuccess(true);
      setDesktop(null);
      setMobile(null);
      setFavicon(null);
      setHomescreen(null);
      setMasterAdmin(null);

      setTimeout(() => clearFlags(), 5 * 1000);
    } catch (err) {
      setError(err.message);
      setTimeout(() => clearFlags(), 15 * 1000);
    }
  }

  return (
    <main id="settings" className={`flex-grow-1 mb-5`}>
      <div className="container mt-5">
        <section className="main dao-settings">
          <SetupSummaryCard />

          {success && (
            <Alert type="success" message="Changes successfully saved" />
          )}

          {error && (
            <Alert type="error" message="Something went wrong" description={error} />
          )}

          <AjaxForm
            submitAction={(data) => submitForm(data)}
            initialValues={{
              name: settings.name,
              acronym: settings.acronym,
              physical_address: settings.physical_address,
              store_url: settings.store_url,
              jurisdiction: settings.jurisdiction,
              support_whatsapp: settings.support_whatsapp,
            timezone: settings.timezone,
            }}
            footer={({ isSubmitting }) => (
              <div className="dao-settings__actions">
                <Button type="submit" variant="primary" size="lg" loading={isSubmitting}>
                  Save settings
                </Button>
              </div>
            )}
          >
            <Card
              as="section"
              padding="lg"
              className="dao-settings__section"
              ref={logos.ref}
              data-setup-target={logos.targeted ? 'true' : undefined}
            >
              <h3 className="dao-settings__section-title">Logos</h3>
              <p className="dao-settings__section-note">
                Fields marked * complete a step on your setup checklist. The other three are
                optional and fall back to your desktop logo.
              </p>

              <div className="dao-settings__logo-row">
                <Field label="Header Desktop (81 x 81)" required hint="The logo at the top of the app on a laptop. This is the one your setup checklist looks for.">
                  <LogoPicker current={settings.desktop} file={desktop} onPick={setDesktop} alt="Header desktop logo" />
                </Field>
              </div>

              <div className="dao-settings__logo-row">
                <Field label="Header Mobile (31 x 31)" hint="The same logo at the top of the app on a phone.">
                  <LogoPicker current={settings.mobile} file={mobile} onPick={setMobile} alt="Header mobile logo" />
                </Field>
              </div>

              <div className="dao-settings__logo-row">
                <Field label="Favicon (16 x 16)" hint="The small icon in the browser tab.">
                  <LogoPicker current={settings.favicon} file={favicon} onPick={setFavicon} alt="Favicon" small />
                </Field>
              </div>

              <div className="dao-settings__logo-row">
                <Field label="Home Screen Icon (180 x 180)" hint="Used when someone saves the app to their phone home screen.">
                  <LogoPicker current={settings.homescreen} file={homescreen} onPick={setHomescreen} alt="Home screen icon" />
                </Field>
              </div>
            </Card>

            <Card as="section" padding="lg" className="dao-settings__section">
              <h3 className="dao-settings__section-title">Labels</h3>
              <p className="dao-settings__section-note">
                Fields marked * complete a step on your setup checklist.
              </p>

              <TextField
                name="name"
                label="Store Name"
                required
                hint="Shown across the app, on your invoices and in the emails your clients receive."
              />
              <TextField
                name="acronym"
                label="Store Acronym"
                hint="A short code used on reference numbers — e.g. Acme Watches becomes AW."
              />
              <AddressField
                name="physical_address"
                label="Store Address"
                required
                hint="Appears on your invoices, your agreements and the footer of your emails."
              />
              <TextField
                name="store_url"
                label="Store URL"
                required
                hint="Where links in your emails and PDFs send your clients. Without it they point at Retail Circle."
              />
              <JurisdictionField
                name="jurisdiction"
                label="Store Country Jurisdiction"
                options={jurisdictionOptions}
                required
                hint="Sets the governing law written into your consignment and purchase agreements."
              />
              <JurisdictionField
                name="timezone"
                label="Time zone"
                options={timezoneOptions}
                placeholder="Select a time zone…"
                hint="Your store's local time. WhatsApp Broadcast can use it for its sending hours."
              />
              <TextField
                name="support_whatsapp"
                label="Support WhatsApp number"
                hint="How clients reach you from the app. Optional."
              />
            </Card>

            {/* The master "Create Product" button moved to Settings →
                Retail Circle Admin → Shared Product Database (store-catalog feature,
                Alex 2026-08-21) — store admins add their own entries under
                Owner Admin → My Products instead. */}

            {/* "Master admin" is a single GLOBAL platform role (is_master_admin),
                not a per-store one — only the platform master admin may see/manage
                it. Hiding it from tenant store admins prevents leaking the platform
                admin's identity onto a tenant's settings page. */}
            {user.is_master_admin && (
              <MasterAdmin
                {...{
                  user,
                  masterAdmin,
                  setMasterAdmin,
                  masterAdminUpdated,
                }}
              />
            )}

          </AjaxForm>
        </section>
      </div>
    </main>
  );
}
